import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronRight, ChevronDown, PanelLeftClose, PanelLeft } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { useSidebar } from '../../../context/SidebarContext';

/**
 * AdminSidebar Component
 *
 * A collapsible sidebar navigation for the admin panel with menu items,
 * active state indicators, badges, and user information.
 *
 * @param {Object} props
 * @param {Array} props.menuItems - Array of menu item objects
 * @param {Array} props.bottomMenuItems - Array of bottom menu item objects
 * @param {Object} props.branding - Branding configuration
 * @param {boolean} props.defaultOpen - Initial sidebar state (default: true)
 */
export default function AdminSidebar({
  menuItems = [],
  bottomMenuItems = [],
  branding = {
    logo: 'eComm Notify',
    subtitle: 'Admin Portal',
    color: 'indigo'
  },
  defaultOpen = true
}) {
  const { isCollapsed, setIsCollapsed, hasProvider } = useSidebar();
  // Use context state if provider is available, otherwise use local state
  const [localSidebarOpen, setLocalSidebarOpen] = useState(defaultOpen);
  const sidebarOpen = hasProvider ? !isCollapsed : localSidebarOpen;
  const setSidebarOpen = (open) => {
    if (hasProvider) {
      setIsCollapsed(!open);
    }
    setLocalSidebarOpen(open);
  };

  const [openMenus, setOpenMenus] = useState({});
  const [hoveredMenu, setHoveredMenu] = useState(null);
  const [hideTimeout, setHideTimeout] = useState(null);
  const navRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();


  // Save sidebar scroll on every scroll event
  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    const handleScroll = () => {
      sessionStorage.setItem('sidebar_scroll', String(nav.scrollTop));
    };
    nav.addEventListener('scroll', handleScroll, { passive: true });
    return () => nav.removeEventListener('scroll', handleScroll);
  }, []);

  // Initialize openMenus for active children AND restore scroll position after menus expand
  useEffect(() => {
    const newOpenMenus = {};

    [...menuItems, ...bottomMenuItems].forEach((item) => {
      if (item.children && item.children.length > 0) {
        const hasActiveChild = item.children.some(
          (child) => location.pathname === child.path
        );
        if (hasActiveChild) {
          newOpenMenus[item.label] = true;
        }
      }
    });

    if (Object.keys(newOpenMenus).length > 0) {
      setOpenMenus((prev) => ({ ...prev, ...newOpenMenus }));
    }

    // Restore scroll after React has re-rendered with expanded menus
    const saved = sessionStorage.getItem('sidebar_scroll');
    if (saved && navRef.current) {
      // Double rAF ensures the DOM has painted after openMenus state update
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (navRef.current) {
            navRef.current.scrollTop = parseInt(saved, 10);
          }
        });
      });
    }
  }, [location.pathname]);

  // Amazon-inspired color scheme
  const colors = {
    gradient: 'from-[#f08804] to-[#e88b00]',
    active: 'bg-[#FFF4E6] text-[#C45500]',
    indicator: 'bg-[#f08804]',
    avatar: 'bg-[#FFF4E6] text-[#C45500]'
  };

  // Get user initials for avatar
  const getUserInitial = () => {
    if (user?.name) {
      return user.name.charAt(0).toUpperCase();
    }
    return 'U';
  };

  // Toggle sidebar
  const handleToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Handle menu item click
  const handleMenuClick = (path, parentLabel = null) => {
    // Keep parent menu open when clicking a child
    if (parentLabel) {
      setOpenMenus((prev) => ({ ...prev, [parentLabel]: true }));
    }
    navigate(path);
  };

  // Check if menu item is active
  const isActive = (path) => {
    if (location.pathname === path) return true;
    // /sellers/:id (legacy direct access) should highlight seller-accounts
    if (path === '/seller-accounts' && location.pathname.startsWith('/sellers/')) return true;
    return false;
  };

  // Handle popover show with delay
  const handlePopoverShow = (menuLabel) => {
    if (hideTimeout) {
      clearTimeout(hideTimeout);
      setHideTimeout(null);
    }
    setHoveredMenu(menuLabel);
  };

  // Handle popover hide with delay
  const handlePopoverHide = () => {
    const timeout = setTimeout(() => {
      setHoveredMenu(null);
    }, 200); // 200ms delay before hiding
    setHideTimeout(timeout);
  };

  // Cancel hide when mouse enters popover
  const handlePopoverEnter = () => {
    if (hideTimeout) {
      clearTimeout(hideTimeout);
      setHideTimeout(null);
    }
  };

  return (
    <>
    {/* Mobile overlay */}
    {sidebarOpen && (
      <div className="fixed inset-0 bg-black/30 z-30 lg:hidden" onClick={handleToggle} />
    )}
    <aside
      className={`${
        sidebarOpen ? 'w-60' : 'w-[52px]'
      } bg-white border-r border-[#E7E7E7] transition-all duration-300 flex flex-col flex-shrink-0 ${
        sidebarOpen ? 'fixed inset-y-0 left-0 z-40 lg:relative lg:z-auto' : 'hidden lg:flex'
      }`}
    >
      {/* Toggle button */}
      <div className="px-2.5 pt-2.5 pb-1">
        <button
          onClick={handleToggle}
          className="w-full flex items-center justify-center gap-2 p-2 rounded-md text-[#565959] hover:bg-[#F7F7F7] transition-colors"
          title={sidebarOpen ? 'Collapse' : 'Expand'}
        >
          {sidebarOpen ? <PanelLeftClose className="w-4 h-4" /> : <PanelLeft className="w-4 h-4" />}
          {sidebarOpen && <span className="text-xs font-medium flex-1 text-left text-[#565959]">Collapse</span>}
        </button>
      </div>

      {/* Menu Items */}
      <nav ref={navRef} className="flex-1 px-2.5 py-1 space-y-0.5 overflow-y-auto custom-scrollbar">
        {/* Main Menu Items */}
        {menuItems.map((item) => {
          const Icon = item.icon;
          const hasChildren = Array.isArray(item.children) && item.children.length > 0;
          const hasActiveChild = hasChildren && item.children.some(
            (child) => location.pathname === child.path
          );
          const active = isActive(item.path) || hasActiveChild;
          const isOpen = openMenus[item.label] ?? false;
          const showPopover = !sidebarOpen && hasChildren && hoveredMenu === item.label;

          return (
            <div
              key={item.path}
              className="relative"
              onMouseEnter={() => !sidebarOpen && hasChildren && handlePopoverShow(item.label)}
              onMouseLeave={() => !sidebarOpen && hasChildren && handlePopoverHide()}
            >
              <button
                onClick={() => {
                  if (hasChildren && sidebarOpen) {
                    setOpenMenus((prev) => ({ ...prev, [item.label]: !prev[item.label] }));
                  } else if (!hasChildren) {
                    handleMenuClick(item.path);
                  }
                }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 group relative focus:outline-none focus:ring-2 focus:ring-[#f08804] focus:ring-offset-1 ${
                  active
                    ? colors.active
                    : 'text-[#565959] hover:bg-[#F7F7F7] hover:text-[#0F1111]'
                }`}
                title={item.description || item.label}
                aria-label={item.label}
                aria-current={active ? 'page' : undefined}
              >
                {/* Active Indicator Bar */}
                {active && !hasChildren && (
                  <div className={`absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 ${colors.indicator} rounded-r-full`} />
                )}

                {/* Icon */}
                <div className={`flex-shrink-0 ${active ? '' : 'group-hover:scale-110'} transition-transform`}>
                  <Icon className="w-5 h-5" />
                </div>

                {/* Label, Badge, and Arrow (when expanded) */}
                {sidebarOpen && (
                  <>
                    <span className="flex-1 text-left font-medium text-sm truncate">
                      {item.label}
                    </span>

                    {/* Badge */}
                    {item.badge && (
                      <span className="px-2 py-0.5 bg-red-500 text-white text-xs rounded-full font-semibold shadow-sm">
                        {item.badge}
                      </span>
                    )}

                    {/* Chevron for expandable menus */}
                    {hasChildren ? (
                      <div className="transition-transform duration-200">
                        {isOpen ? (
                          <ChevronDown className="w-4 h-4 text-slate-500" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-slate-500" />
                        )}
                      </div>
                    ) : (
                      active && <ChevronRight className="w-4 h-4 flex-shrink-0" />
                    )}
                  </>
                )}

                {/* Tooltip Badge for Collapsed State */}
                {!sidebarOpen && item.badge && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold shadow-md">
                    {item.badge}
                  </span>
                )}

                {/* Active badge indicator when collapsed and has active child */}
                {!sidebarOpen && hasActiveChild && (
                  <span className="absolute -top-1 -right-1 w-2 h-2 bg-[#f08804] rounded-full"></span>
                )}
              </button>

              {/* SUBMENU - Expanded Sidebar */}
              {hasChildren && isOpen && sidebarOpen && (
                <div className="mt-1 ml-3 pl-3 border-l-2 border-slate-200 space-y-0.5">
                  {item.children.map((child) => {
                    const ChildIcon = child.icon;
                    const childActive = location.pathname === child.path;

                    return (
                      <button
                        key={child.path}
                        onClick={() => handleMenuClick(child.path, item.label)}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 group relative focus:outline-none focus:ring-2 focus:ring-[#f08804] focus:ring-offset-1 ${
                          childActive
                            ? colors.active
                            : 'text-[#565959] hover:bg-[#F7F7F7] hover:text-[#0F1111]'
                        }`}
                        title={child.description || child.label}
                        aria-current={childActive ? 'page' : undefined}
                      >
                        {/* Active Indicator Bar for submenu */}
                        {childActive && (
                          <div className={`absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 ${colors.indicator} rounded-r-full`} />
                        )}

                        {/* Child Icon */}
                        <div className="flex-shrink-0 group-hover:scale-110 transition-transform">
                          <ChildIcon className="w-4 h-4" />
                        </div>

                        {/* Child Label */}
                        <span className="flex-1 text-left truncate">{child.label}</span>

                        {/* Active Arrow */}
                        {childActive && (
                          <ChevronRight className="w-4 h-4 flex-shrink-0" />
                        )}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* SUBMENU POPOVER - Collapsed Sidebar */}
              {showPopover && (() => {
                const buttonElement = document.querySelector(`button[aria-label="${item.label}"]`);
                const rect = buttonElement?.getBoundingClientRect();

                return (
                  <div
                    className="fixed bg-white rounded-lg shadow-xl border border-slate-200 py-2 px-1 min-w-[200px] z-[100]"
                    style={{
                      left: `${(rect?.right || 0) + 8}px`,
                      top: `${rect?.top || 0}px`,
                    }}
                    onMouseEnter={handlePopoverEnter}
                    onMouseLeave={handlePopoverHide}
                  >
                    {/* Popover Header */}
                    <div className="px-3 py-2 border-b border-slate-100 mb-1">
                      <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                        {item.label}
                      </p>
                    </div>

                    {/* Popover Menu Items */}
                    <div className="space-y-0.5">
                      {item.children.map((child) => {
                        const ChildIcon = child.icon;
                        const childActive = location.pathname === child.path;

                        return (
                          <button
                            key={child.path}
                            onClick={() => handleMenuClick(child.path, item.label)}
                            className={`w-full flex items-center gap-3 px-3 py-2.5 mx-1 rounded-lg text-sm font-medium transition-all duration-200 group relative ${
                              childActive
                                ? colors.active
                                : 'text-[#565959] hover:bg-[#F7F7F7] hover:text-[#0F1111]'
                            }`}
                          >
                            {/* Active Indicator */}
                            {childActive && (
                              <div className={`absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 ${colors.indicator} rounded-r-full`} />
                            )}

                            {/* Child Icon */}
                            <div className="flex-shrink-0 group-hover:scale-110 transition-transform">
                              <ChildIcon className="w-4 h-4" />
                            </div>

                            {/* Child Label */}
                            <span className="flex-1 text-left truncate">{child.label}</span>

                            {/* Active Arrow */}
                            {childActive && (
                              <ChevronRight className="w-4 h-4 flex-shrink-0" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
            </div>
          );
        })}

        {/* Divider (only if bottom menu items exist) */}
        {bottomMenuItems.length > 0 && (
          <div className="py-2">
            <div className="h-px bg-slate-200" />
          </div>
        )}

 {/* Bottom Menu Items */}
{bottomMenuItems.map((item, index) => {
  const Icon = item.icon;
  const hasChildren = Array.isArray(item.children) && item.children.length > 0;
  const hasActiveChild = hasChildren && item.children.some(
    (child) => location.pathname === child.path
  );
  const isOpen = openMenus[item.label] ?? false;
  const showPopover = !sidebarOpen && hasChildren && hoveredMenu === item.label;

  return (
    <div
      key={item.path}
      className="relative"
      onMouseEnter={() => !sidebarOpen && hasChildren && handlePopoverShow(item.label)}
      onMouseLeave={() => !sidebarOpen && hasChildren && handlePopoverHide()}
    >
      {/* MAIN MENU BUTTON */}
      <button
        onClick={() => {
          if (hasChildren && sidebarOpen) {
            setOpenMenus((prev) => ({ ...prev, [item.label]: !prev[item.label] }));
          } else if (!hasChildren) {
            handleMenuClick(item.path);
          }
        }}
        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 group relative focus:outline-none focus:ring-2 focus:ring-[#f08804] focus:ring-offset-1 ${
          location.pathname === item.path
            ? colors.active
            : 'text-[#565959] hover:bg-[#F7F7F7] hover:text-[#0F1111]'
        }`}
        title={item.description || item.label}
        aria-label={item.label}
      >
        {/* Active Indicator Bar (only when directly on this path) */}
        {location.pathname === item.path && (
          <div className={`absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 ${colors.indicator} rounded-r-full`} />
        )}

        {/* Icon */}
        <div className="flex-shrink-0 group-hover:scale-110 transition-transform">
          <Icon className="w-5 h-5" />
        </div>

        {/* Label and Arrow (when expanded) */}
        {sidebarOpen && (
          <>
            <span className="flex-1 text-left font-medium text-sm truncate">
              {item.label}
            </span>

            {/* Chevron Arrow for expandable menus */}
            {hasChildren && (
              <div className="transition-transform duration-200">
                {isOpen ? (
                  <ChevronDown className="w-4 h-4 text-slate-500" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-slate-500" />
                )}
              </div>
            )}
          </>
        )}

        {/* Active badge indicator when collapsed and has active child */}
        {!sidebarOpen && hasActiveChild && (
          <span className="absolute -top-1 -right-1 w-2 h-2 bg-[#f08804] rounded-full"></span>
        )}
      </button>

      {/* SUBMENU - Expanded Sidebar */}
      {hasChildren && isOpen && sidebarOpen && (
        <div className="mt-1 ml-3 pl-3 border-l-2 border-slate-200 space-y-0.5">
          {item.children.map((child) => {
            const ChildIcon = child.icon;
            const childActive = location.pathname === child.path;

            return (
              <button
                key={child.path}
                onClick={() => handleMenuClick(child.path, item.label)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 group relative focus:outline-none focus:ring-2 focus:ring-[#f08804] focus:ring-offset-1 ${
                  childActive
                    ? colors.active
                    : 'text-[#565959] hover:bg-[#F7F7F7] hover:text-[#0F1111]'
                }`}
                title={child.description || child.label}
                aria-current={childActive ? 'page' : undefined}
              >
                {/* Active Indicator Bar for submenu */}
                {childActive && (
                  <div className={`absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 ${colors.indicator} rounded-r-full`} />
                )}

                {/* Child Icon */}
                <div className="flex-shrink-0 group-hover:scale-110 transition-transform">
                  <ChildIcon className="w-4 h-4" />
                </div>

                {/* Child Label */}
                <span className="flex-1 text-left truncate">{child.label}</span>

                {/* Active Arrow */}
                {childActive && (
                  <ChevronRight className="w-4 h-4 flex-shrink-0" />
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* SUBMENU POPOVER - Collapsed Sidebar */}
      {showPopover && (() => {
        const buttonElement = document.querySelector(`button[aria-label="${item.label}"]`);
        const rect = buttonElement?.getBoundingClientRect();

        return (
          <div
            className="fixed bg-white rounded-lg shadow-xl border border-slate-200 py-2 px-1 min-w-[200px] z-[100]"
            style={{
              left: `${(rect?.right || 0) + 8}px`,
              top: `${rect?.top || 0}px`,
            }}
            onMouseEnter={handlePopoverEnter}
            onMouseLeave={handlePopoverHide}
          >
            {/* Popover Header */}
            <div className="px-3 py-2 border-b border-slate-100 mb-1">
              <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                {item.label}
              </p>
            </div>

            {/* Popover Menu Items */}
            <div className="space-y-0.5">
              {item.children.map((child) => {
                const ChildIcon = child.icon;
                const childActive = location.pathname === child.path;

                return (
                  <button
                    key={child.path}
                    onClick={() => handleMenuClick(child.path, item.label)}
                    title={child.description || child.label}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 mx-1 rounded-lg text-sm font-medium transition-all duration-200 group relative ${
                      childActive
                        ? colors.active
                        : 'text-[#565959] hover:bg-[#F7F7F7] hover:text-[#0F1111]'
                    }`}
                  >
                    {/* Active Indicator */}
                    {childActive && (
                      <div className={`absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 ${colors.indicator} rounded-r-full`} />
                    )}

                    {/* Child Icon */}
                    <div className="flex-shrink-0 group-hover:scale-110 transition-transform">
                      <ChildIcon className="w-4 h-4" />
                    </div>

                    {/* Child Label */}
                    <span className="flex-1 text-left truncate">{child.label}</span>

                    {/* Active Arrow */}
                    {childActive && (
                      <ChevronRight className="w-4 h-4 flex-shrink-0" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })()}
    </div>
  );
})}


      </nav>

    </aside>
    </>
  );
}